// Libraries
import { useMemo, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { FileSearch02 } from "@untitled-ui/icons-react";

// Resources
import { ReactComponent as AddLine } from "#src/assets/images/icons/add-line.svg";

// General
import constants from "#src/config/constants";

// Components
import ConfigSectionWrapperV2 from "#src/commonV2/helper/wrapper/ConfigSection";
import { toPascalCase } from "#src/utils/common";
import { employeeSizeList } from "#src/common/helper/contactSupport/contactDetailFields";
import { FormInputV2 } from "#src/commonV2/form/Input";
import { FormTextArea } from "#src/commonV2/form/TextArea";
import { FormSelectV2 } from "#src/commonV2/form/Select";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { ButtonV2 } from "#src/commonV2/Button";
import { SocialProfileItem } from "./SocialProfileItem";

interface ISocialProfile {
  name: string;
  url: string;
}
export interface IWorkspaceAdditionalForm {
  social: ISocialProfile[];
  employee_size: string;
  industry: string;
  technologies: string;
}

interface IAdditionalInformationProps {
  callingAPI: boolean;
  allowedToUpdate: boolean;
  information: IWorkspaceAdditionalForm;
  getFieldError: (fieldKey: string, formData: any) => string | null;
  onChangeInformation: Dispatch<SetStateAction<IWorkspaceAdditionalForm>>;
}

const WorkspaceAdditionalInformation = ({
  callingAPI = false,
  allowedToUpdate,
  information,
  getFieldError,
  onChangeInformation,
}: IAdditionalInformationProps) => {
  const { t } = useTranslation("workspace", { keyPrefix: "detail" });

  const onAddNewSocialProfile = () => {
    const newSocialProfile = constants.AVAILABLE_SOCIAL_NAME.filter(
      (item) => !information.social.map((sp) => sp.name).includes(item)
    )[0];
    onChangeInformation({
      ...information,
      social: [
        ...information.social,
        {
          name: newSocialProfile,
          url: "https://",
        },
      ],
    });
  };

  const onRemoveSocial = (index: number) => {
    onChangeInformation({
      ...information,
      social: information.social.filter((s, i) => index !== i),
    });
  };

  const onUpdateSocial = (index: number, value: ISocialProfile) => {
    onChangeInformation({
      ...information,
      social: information.social.map((s, i) => {
        if (i === index) {
          return value;
        }
        return s;
      }),
    });
  };

  const socialItems: IFilterItemV2[] = constants.AVAILABLE_SOCIAL_NAME.map(
    (item) => ({
      key: item,
      value: item,
      getLabel: () => {
        return toPascalCase(item);
      },
    })
  );

  const disabled = useMemo(() => {
    return !allowedToUpdate || callingAPI;
  }, [allowedToUpdate, callingAPI]);

  const selectedEmployee: IFilterItemV2 = useMemo(() => {
    return (
      employeeSizeList.find((e) => e.key === information.employee_size) || {
        key: "",
        value: "",
        getLabel: () => {
          return "";
        },
      }
    );
  }, [information.employee_size]);

  return (
    <ConfigSectionWrapperV2
      icon={<FileSearch02 className="h-5 w-5 text-gray-v2-500" />}
      headerTitle={t("additionalInformation.title")}
    >
      <div className="grid grid-cols-2 gap-8 p-4">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-gray-v2-700">
              {t("additionalInformation.form.social")}
            </span>
            {information.social.map((socialItem, index) => {
              const selectedSocial = socialItems.find(
                (item) => item.key === socialItem.name
              ) || {
                key: "",
                value: "",
                getLabel: () => {
                  return "";
                },
              };

              return (
                <SocialProfileItem
                  key={index}
                  value={socialItem.url}
                  onChangeValue={(v) =>
                    onUpdateSocial(index, { ...socialItem, url: v })
                  }
                  selected={selectedSocial}
                  options={socialItems.filter(
                    (i) =>
                      !information.social.map((s) => s.name).includes(i.key) ||
                      i.key === socialItem.name
                  )}
                  onChangeSelected={(value) =>
                    onUpdateSocial(index, { ...socialItem, name: value.key })
                  }
                  onRemove={() => onRemoveSocial(index)}
                />
              );
            })}
          </div>
          {allowedToUpdate ? (
            <ButtonV2
              variant="secondary"
              className="w-full"
              onClick={onAddNewSocialProfile}
            >
              <AddLine />
              {t("button.addMoreSocialProfile")}
            </ButtonV2>
          ) : null}
        </div>
        <div className="flex flex-col gap-6">
          <FormInputV2
            label={t("additionalInformation.form.industry")}
            placeholder={t("additionalInformation.form.placeholderIndustry")}
            value={information.industry}
            disabled={disabled}
            error={getFieldError("industry", information)}
            onChangeValue={(v) =>
              onChangeInformation({ ...information, industry: v })
            }
          />
          <FormTextArea
            label={t("additionalInformation.form.technology")}
            placeholder={t("additionalInformation.form.placeholderTechnology")}
            value={information.technologies}
            disabled={disabled}
            rows={2}
            error={getFieldError("technologies", information)}
            onChangeValue={(v) =>
              onChangeInformation({ ...information, technologies: v })
            }
          />
          <FormSelectV2
            label={t("additionalInformation.form.employeeSize")}
            placeholder={t(
              "additionalInformation.form.placeholderEmployeeSize"
            )}
            options={employeeSizeList}
            selected={selectedEmployee}
            onChangeSelected={(v) =>
              onChangeInformation({
                ...information,
                employee_size: v.key,
              })
            }
          />
        </div>
      </div>
    </ConfigSectionWrapperV2>
  );
};

export default WorkspaceAdditionalInformation;
